import type { Capabilities } from "@fistos/shared";
import type { DeviceEventInfo } from "@fistos/shared/api";
import { Bell, Check, MessageSquare, Phone, Trash2, X } from "lucide-solid";
import { createMemo, createResource, For, Show, type JSX } from "solid-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { now } from "@/lib/clock";
import { ago, clock } from "@/lib/format";
import { live, seedEvents } from "@/lib/live";
import { parse } from "@/lib/proto";
import type { PanelProps } from "./common";

function EventRow(props: { e: DeviceEventInfo }) {
  const view = (): { icon: JSX.Element; title: string; text: string } => {
    const e = props.e;
    if (e.kind === "notification") {
      const n = parse.notification(e.data);
      return { icon: <Bell />, title: n.title || n.packageName, text: n.text || n.packageName };
    }
    if (e.kind === "sms") {
      const m = parse.sms(e.data);
      return {
        icon: <MessageSquare />,
        title: `${m.incoming ? "SMS from" : "SMS to"} ${m.contactName || m.address}`,
        text: m.body,
      };
    }
    const c = parse.call(e.data);
    return {
      icon: <Phone />,
      title: `Call ${c.incoming ? "from" : "to"} ${c.contactName || c.number || "unknown"}`,
      text: "",
    };
  };
  return (
    <div class="flex gap-3 py-2.5">
      <div class="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-full [&_svg]:size-4">
        {view().icon}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex justify-between gap-2 text-sm">
          <span class="truncate font-medium">{view().title}</span>
          <span class="text-muted-foreground shrink-0 text-xs tabular-nums">{clock(props.e.at)}</span>
        </div>
        <Show when={view().text}>
          <p class="text-muted-foreground line-clamp-2 text-sm break-words">{view().text}</p>
        </Show>
      </div>
    </div>
  );
}

function capRows(c: Capabilities): [string, boolean | string][] {
  return [
    ["Accessibility (UI tree)", c.accessibility],
    ["AppFunctions", c.appFunctions],
    ["Screenshots", c.screenshot],
    ["Input injection", c.inputInjection],
    ["Call control", c.callControl],
    ["Call audio (listen)", c.callDownlink || "none"],
    ["Call audio (speak)", c.callUplinkInjection],
    ["Call forwarding", c.callForwarding],
    ["SMS send", c.smsSend],
    ["SMS receive", c.smsReceive],
    ["RCS", c.rcs],
    ["Camera", c.camera],
  ];
}

export function ActivityPanel(props: PanelProps & { onRemove: () => void }) {
  createResource(
    () => props.device.id,
    async (id) => seedEvents(id, await api.events(id)),
  );
  const events = createMemo(() => [...(live.events[props.device.id] ?? [])].reverse());
  return (
    <div class="grid gap-4 *:min-w-0 lg:grid-cols-5">
      <Card class="gap-2 py-5 lg:col-span-3">
        <CardHeader class="px-5">
          <CardTitle class="text-base">Recent events</CardTitle>
        </CardHeader>
        <CardContent class="px-5">
          <Show
            when={events().length}
            fallback={
              <p class="text-muted-foreground text-sm">
                Notifications, messages and calls show up here. The server keeps only the latest few in memory.
              </p>
            }
          >
            <div class="divide-y">
              <For each={events()}>{(e) => <EventRow e={e} />}</For>
            </div>
          </Show>
        </CardContent>
      </Card>
      <div class="grid content-start gap-4 lg:col-span-2">
        <Card class="gap-3 py-5">
          <CardHeader class="px-5">
            <CardTitle class="text-base">Capabilities</CardTitle>
          </CardHeader>
          <CardContent class="grid gap-1.5 px-5 text-sm">
            <Show when={props.caps} fallback={<p class="text-muted-foreground text-sm">Not reported yet.</p>}>
              {(c) => (
                <For each={capRows(c())}>
                  {([name, v]) => (
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-muted-foreground">{name}</span>
                      <Show
                        when={typeof v === "string"}
                        fallback={
                          v ? <Check class="text-success size-4" /> : <X class="text-muted-foreground/60 size-4" />
                        }
                      >
                        <span class="font-mono text-xs">{v as string}</span>
                      </Show>
                    </div>
                  )}
                </For>
              )}
            </Show>
          </CardContent>
        </Card>
        <Card class="gap-3 py-5">
          <CardHeader class="px-5">
            <CardTitle class="text-base">Device</CardTitle>
          </CardHeader>
          <CardContent class="grid gap-2 px-5 text-sm">
            <div class="grid gap-0.5">
              <span class="text-muted-foreground text-xs">Device id (key fingerprint)</span>
              <code class="text-xs break-all">{props.device.id}</code>
            </div>
            <div class="grid gap-0.5">
              <span class="text-muted-foreground text-xs">Build</span>
              <code class="text-xs break-all">{props.device.buildFingerprint || "—"}</code>
            </div>
            <div class="flex justify-between">
              <span class="text-muted-foreground text-xs">Enrolled</span>
              <span class="text-xs">{ago(props.device.enrolledAt, now())}</span>
            </div>
            <Button variant="outline" class="text-destructive mt-2" onClick={props.onRemove}>
              <Trash2 /> Remove device
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
